import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, ScatterChart, Scatter,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, AreaChart, Area
} from 'recharts';
import { 
  TrendingUp, TrendingDown, DollarSign, Clock, Users, Building2, 
  Filter, Download, FileText, FileSpreadsheet, RefreshCw, AlertTriangle,
  CheckCircle, XCircle, Hourglass, Target, Activity, Mail, ChevronRight,
  Calendar, BarChart3, PieChart as PieChartIcon, Loader2, Home, MapPin
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

// Color palette
const COLORS = {
  go: '#10B981',
  nogo: '#EF4444',
  pending: '#F59E0B',
  primary: '#3B82F6',
  secondary: '#8B5CF6',
  accent: '#EC4899'
};

const PIE_COLORS = [COLORS.go, COLORS.nogo, COLORS.pending];

// Format currency
const formatCurrency = (value) => {
  if (!value && value !== 0) return '-';
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

// KPI Card Component
const KPICard = ({ title, value, subtitle, icon: Icon, trend, color = 'blue' }) => {
  const iconChip = {
    blue: 'bg-blue-50 dark:bg-blue-900/30 text-blue-600',
    green: 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600',
    red: 'bg-red-50 dark:bg-red-900/30 text-red-600',
    amber: 'bg-amber-50 dark:bg-amber-900/30 text-amber-600',
    purple: 'bg-purple-50 dark:bg-purple-900/30 text-purple-600'
  }[color] || 'bg-slate-50 text-slate-600';

  return (
    <Card className="border-slate-200 dark:border-slate-800 shadow-sm">
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide truncate">{title}</p>
            <p className="text-2xl sm:text-3xl font-semibold tabular-nums tracking-tight mt-1 text-slate-900 dark:text-white">{value}</p>
            {subtitle && <p className="text-[11px] text-slate-400 mt-0.5 truncate">{subtitle}</p>}
          </div>
          <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${iconChip}`}>
            <Icon className="w-4 h-4" />
          </div>
        </div>
        {trend !== undefined && (
          <div className="flex items-center gap-1 mt-2">
            {trend >= 0 ? (
              <TrendingUp className="w-3 h-3 text-emerald-500" />
            ) : (
              <TrendingDown className="w-3 h-3 text-red-500" />
            )}
            <span className={`text-xs ${trend >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
              {trend >= 0 ? '+' : ''}{trend}%
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default function AnalyticsDashboardPage() {
  const navigate = useNavigate();
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  
  // Data states
  const [overview, setOverview] = useState(null);
  const [monthlyData, setMonthlyData] = useState([]);
  const [retailerData, setRetailerData] = useState([]);
  const [commercialData, setCommercialData] = useState([]);
  const [pipelineData, setPipelineData] = useState(null);
  const [operationalData, setOperationalData] = useState(null);
  const [scatterData, setScatterData] = useState([]);
  const [filterOptions, setFilterOptions] = useState({ retailers: [], commercials: [], statuses: [] });
  const [mapData, setMapData] = useState([]);
  const [loadingMap, setLoadingMap] = useState(false);
  
  // Filter states
  const [filters, setFilters] = useState({
    period: 'month',
    retailer: 'all',
    commercial: 'all',
    status: 'all'
  });
  
  // Email filter state
  const [emailFilter, setEmailFilter] = useState('all');

  // Calculate date range based on period
  const getDateRange = useCallback(() => {
    const now = new Date();
    let startDate = null;
    let endDate = now.toISOString();
    
    switch (filters.period) {
      case 'month':
        startDate = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
        break;
      case 'quarter':
        const quarterStart = Math.floor(now.getMonth() / 3) * 3;
        startDate = new Date(now.getFullYear(), quarterStart, 1).toISOString();
        break;
      case 'year':
        startDate = new Date(now.getFullYear(), 0, 1).toISOString();
        break;
      case 'all':
      default:
        startDate = null;
        endDate = null;
    }
    
    return { startDate, endDate };
  }, [filters.period]);

  // Fetch all analytics data
  const fetchAnalyticsData = useCallback(async () => {
    setLoading(true);
    try {
      const { startDate, endDate } = getDateRange();
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      if (filters.retailer !== 'all') params.append('retailer', filters.retailer);
      if (filters.commercial !== 'all') params.append('commercial_id', filters.commercial);
      if (filters.status !== 'all') params.append('status', filters.status);
      
      const queryString = params.toString();
      
      // Fetch all data in parallel
      const [
        overviewRes,
        monthlyRes,
        retailerRes,
        commercialRes,
        pipelineRes,
        operationalRes,
        scatterRes,
        filtersRes
      ] = await Promise.all([
        api().get(`/analytics/overview?${queryString}`),
        api().get(`/analytics/monthly-evolution?${queryString}`),
        api().get(`/analytics/by-retailer?${queryString}`),
        api().get(`/analytics/by-commercial?${queryString}`),
        api().get('/analytics/pipeline'),
        api().get(`/analytics/operational?${queryString}`),
        api().get(`/analytics/scatter-data?${queryString}`),
        api().get('/analytics/filters-options')
      ]);
      
      setOverview(overviewRes.data);
      setMonthlyData(monthlyRes.data);
      setRetailerData(retailerRes.data);
      setCommercialData(commercialRes.data);
      setPipelineData(pipelineRes.data);
      setOperationalData(operationalRes.data);
      setScatterData(scatterRes.data);
      setFilterOptions(filtersRes.data);
      
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
      if (error.response?.status === 403) {
        toast.error('Accès réservé aux Super Admins');
        navigate('/dashboard');
      } else {
        toast.error('Erreur lors du chargement des données');
      }
    } finally {
      setLoading(false);
    }
  }, [getDateRange, filters, navigate]);

  useEffect(() => {
    fetchAnalyticsData();
  }, [fetchAnalyticsData]);

  // Export functions
  const handleExportPDF = async () => {
    setExporting(true);
    try {
      const { startDate, endDate } = getDateRange();
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      if (filters.retailer !== 'all') params.append('retailer', filters.retailer);
      
      const response = await api().get(`/analytics/export/pdf?${params.toString()}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `analytics_report_${new Date().toISOString().slice(0,10)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Rapport PDF téléchargé');
    } catch (error) {
      toast.error('Erreur lors de l\'export PDF');
    } finally {
      setExporting(false);
    }
  };

  const handleExportExcel = async () => {
    setExporting(true);
    try {
      const { startDate, endDate } = getDateRange();
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      if (filters.retailer !== 'all') params.append('retailer', filters.retailer);
      
      const response = await api().get(`/analytics/export/excel?${params.toString()}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `analytics_export_${new Date().toISOString().slice(0,10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Export Excel téléchargé');
    } catch (error) {
      toast.error('Erreur lors de l\'export Excel');
    } finally {
      setExporting(false);
    }
  };

  // Map data
  const fetchMapData = async () => {
    setLoadingMap(true);
    try {
      const response = await api().get('/analytics/map-data');
      setMapData(response.data);
    } catch (e) { console.error(e); }
    finally { setLoadingMap(false); }
  };

  // Pie chart data
  const pieData = overview ? [
    { name: 'GO', value: overview.go_count, color: COLORS.go },
    { name: 'NO GO', value: overview.nogo_count, color: COLORS.nogo },
    { name: 'En attente', value: overview.pending_count, color: COLORS.pending }
  ] : [];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-500 mx-auto" />
          <p className="mt-4 text-slate-500">Chargement des analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 p-4 lg:p-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 dark:text-white flex items-center gap-2">
            <BarChart3 className="w-7 h-7 text-blue-500" />
            Dashboard Analytique
          </h1>
          <p className="text-sm text-slate-500 mt-1">Vue stratégique des performances CAPEX</p>
        </div>
        
        {/* Export buttons */}
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/dashboard')}
            className="gap-2"
          >
            <Home className="w-4 h-4" />
            Retour accueil
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleExportPDF}
            disabled={exporting}
            className="gap-2"
          >
            <FileText className="w-4 h-4" />
            Export PDF
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleExportExcel}
            disabled={exporting}
            className="gap-2"
          >
            <FileSpreadsheet className="w-4 h-4" />
            Export Excel
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={fetchAnalyticsData}
            className="gap-2"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-slate-400" />
              <span className="text-sm font-medium text-slate-600">Filtres:</span>
            </div>
            
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-slate-400" />
              <Select value={filters.period} onValueChange={(v) => setFilters(f => ({...f, period: v}))}>
                <SelectTrigger className="w-[140px] h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="month">Ce mois</SelectItem>
                  <SelectItem value="quarter">Ce trimestre</SelectItem>
                  <SelectItem value="year">Cette année</SelectItem>
                  <SelectItem value="all">Tout</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-center gap-2">
              <Building2 className="w-4 h-4 text-slate-400" />
              <Select value={filters.retailer} onValueChange={(v) => setFilters(f => ({...f, retailer: v}))}>
                <SelectTrigger className="w-[160px] h-9">
                  <SelectValue placeholder="Enseigne" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes enseignes</SelectItem>
                  {filterOptions.retailers.map(r => (
                    <SelectItem key={r} value={r}>{r}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-slate-400" />
              <Select value={filters.commercial} onValueChange={(v) => setFilters(f => ({...f, commercial: v}))}>
                <SelectTrigger className="w-[160px] h-9">
                  <SelectValue placeholder="Commercial" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous</SelectItem>
                  {filterOptions.commercials.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-center gap-2">
              <Target className="w-4 h-4 text-slate-400" />
              <Select value={filters.status} onValueChange={(v) => setFilters(f => ({...f, status: v}))}>
                <SelectTrigger className="w-[140px] h-9">
                  <SelectValue placeholder="Statut" />
                </SelectTrigger>
                <SelectContent>
                  {filterOptions.statuses.map(s => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs defaultValue="performance" className="space-y-6">
        <TabsList className="grid grid-cols-5 w-full max-w-3xl">
          <TabsTrigger value="performance" className="gap-2">
            <TrendingUp className="w-4 h-4" />
            <span className="hidden sm:inline">Performance</span>
          </TabsTrigger>
          <TabsTrigger value="comparatif" className="gap-2">
            <BarChart3 className="w-4 h-4" />
            <span className="hidden sm:inline">Comparatif</span>
          </TabsTrigger>
          <TabsTrigger value="pipeline" className="gap-2">
            <Target className="w-4 h-4" />
            <span className="hidden sm:inline">Pipeline</span>
          </TabsTrigger>
          <TabsTrigger value="operational" className="gap-2">
            <Activity className="w-4 h-4" />
            <span className="hidden sm:inline">Opérationnel</span>
          </TabsTrigger>
          <TabsTrigger value="carte" className="gap-2" data-testid="map-tab">
            <MapPin className="w-4 h-4" />
            <span className="hidden sm:inline">Carte</span>
          </TabsTrigger>
        </TabsList>

        {/* Tab 1: Performance */}
        <TabsContent value="performance" className="space-y-6">
          {/* KPI Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            <KPICard
              title="Total Dossiers"
              value={overview?.total_simulations || 0}
              icon={FileText}
              color="blue"
            />
            <KPICard
              title="GO"
              value={overview?.go_count || 0}
              subtitle={`${overview?.conversion_rate || 0}% conversion`}
              icon={CheckCircle}
              color="green"
            />
            <KPICard
              title="NO GO"
              value={overview?.nogo_count || 0}
              icon={XCircle}
              color="red"
            />
            <KPICard
              title="CA Total"
              value={formatCurrency(overview?.total_ca || 0)}
              icon={DollarSign}
              color="purple"
            />
            <KPICard
              title="Payback Moyen"
              value={`${overview?.avg_payback_months || 0} mois`}
              icon={Clock}
              color="amber"
            />
          </div>

          {/* Charts Row */}
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Monthly Evolution */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-blue-500" />
                  Évolution Mensuelle
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={monthlyData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="month_label" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Legend />
                    <Area type="monotone" dataKey="created" name="Créés" stroke={COLORS.primary} fill={COLORS.primary} fillOpacity={0.3} />
                    <Area type="monotone" dataKey="go" name="GO" stroke={COLORS.go} fill={COLORS.go} fillOpacity={0.3} />
                  </AreaChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Pie Chart - Status Distribution */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <PieChartIcon className="w-5 h-5 text-purple-500" />
                  Répartition des Statuts
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={5}
                      dataKey="value"
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Tab 2: Comparatif */}
        <TabsContent value="comparatif" className="space-y-6">
          <div className="grid lg:grid-cols-2 gap-6">
            {/* By Retailer Bar Chart */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-emerald-500" />
                  Par Enseigne
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={350}>
                  <BarChart data={retailerData.slice(0, 8)} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis type="number" tick={{ fontSize: 11 }} />
                    <YAxis dataKey="retailer" type="category" width={100} tick={{ fontSize: 10 }} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="go" name="GO" fill={COLORS.go} />
                    <Bar dataKey="nogo" name="NO GO" fill={COLORS.nogo} />
                    <Bar dataKey="pending" name="En attente" fill={COLORS.pending} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* By Commercial */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Users className="w-5 h-5 text-purple-500" />
                  Par Commercial
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 max-h-[350px] overflow-y-auto">
                  {commercialData.slice(0, 10).map((c, idx) => (
                    <div key={c.user_id} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-400 to-purple-600 flex items-center justify-center text-white font-bold text-sm">
                          {idx + 1}
                        </div>
                        <div>
                          <p className="font-medium text-sm">{c.name}</p>
                          <p className="text-xs text-slate-500">{c.count} dossiers</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-center">
                          <p className="text-lg font-bold text-emerald-500">{c.go}</p>
                          <p className="text-[10px] text-slate-400">GO</p>
                        </div>
                        <div className="text-center">
                          <p className="text-sm font-semibold text-blue-500">{c.conversion_rate}%</p>
                          <p className="text-[10px] text-slate-400">Conv.</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Scatter Plot */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Target className="w-5 h-5 text-pink-500" />
                CAPEX vs Payback
              </CardTitle>
              <CardDescription>Chaque point représente un projet</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={350}>
                <ScatterChart>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="capex" name="CAPEX" unit=" €" tick={{ fontSize: 11 }} />
                  <YAxis dataKey="payback" name="Payback" unit=" mois" tick={{ fontSize: 11 }} />
                  <Tooltip 
                    cursor={{ strokeDasharray: '3 3' }}
                    content={({ payload }) => {
                      if (payload && payload.length) {
                        const data = payload[0].payload;
                        return (
                          <div className="bg-white dark:bg-slate-800 p-3 rounded-lg shadow-lg border">
                            <p className="font-medium text-sm">{data.name}</p>
                            <p className="text-xs text-slate-500">{data.retailer}</p>
                            <p className="text-xs mt-1">CAPEX: {formatCurrency(data.capex)}</p>
                            <p className="text-xs">Payback: {data.payback} mois</p>
                            <Badge variant={data.status === 'approved' ? 'default' : data.status === 'rejected' ? 'destructive' : 'secondary'} className="mt-1">
                              {data.status === 'approved' ? 'GO' : data.status === 'rejected' ? 'NO GO' : 'En attente'}
                            </Badge>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  <Scatter 
                    data={scatterData}
                    fill={COLORS.primary}
                  >
                    {scatterData.map((entry, index) => (
                      <Cell 
                        key={`cell-${index}`} 
                        fill={entry.status === 'approved' ? COLORS.go : entry.status === 'rejected' ? COLORS.nogo : COLORS.pending}
                      />
                    ))}
                  </Scatter>
                </ScatterChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Retailer Table */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Tableau comparatif par Enseigne</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-slate-50 dark:bg-slate-800">
                      <th className="text-left p-3 font-semibold">Enseigne</th>
                      <th className="text-center p-3 font-semibold">Dossiers</th>
                      <th className="text-center p-3 font-semibold">GO</th>
                      <th className="text-center p-3 font-semibold">NO GO</th>
                      <th className="text-center p-3 font-semibold">Conv. %</th>
                      <th className="text-right p-3 font-semibold">CA Total</th>
                      <th className="text-center p-3 font-semibold">Payback Moy.</th>
                      <th className="text-center p-3 font-semibold">Comm. Moy.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {retailerData.map((r, idx) => (
                      <tr key={r.retailer} className={idx % 2 === 0 ? 'bg-white dark:bg-slate-900' : 'bg-slate-50 dark:bg-slate-800/50'}>
                        <td className="p-3 font-medium">{r.retailer}</td>
                        <td className="p-3 text-center">{r.count}</td>
                        <td className="p-3 text-center text-emerald-600 font-semibold">{r.go}</td>
                        <td className="p-3 text-center text-red-500">{r.nogo}</td>
                        <td className="p-3 text-center">
                          <Badge variant={r.conversion_rate >= 50 ? 'default' : 'secondary'}>
                            {r.conversion_rate}%
                          </Badge>
                        </td>
                        <td className="p-3 text-right font-mono">{formatCurrency(r.total_ca)}</td>
                        <td className="p-3 text-center">{r.avg_payback} mois</td>
                        <td className="p-3 text-center">{r.avg_commission}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab 3: Pipeline */}
        <TabsContent value="pipeline" className="space-y-6">
          {pipelineData && (
            <>
              {/* Pipeline KPIs */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <KPICard
                  title="Dossiers en cours"
                  value={pipelineData.pipeline.count}
                  icon={Hourglass}
                  color="amber"
                />
                <KPICard
                  title="CA Pipeline"
                  value={formatCurrency(pipelineData.pipeline.total_ca)}
                  subtitle="Valeur si tout GO"
                  icon={DollarSign}
                  color="green"
                />
                <KPICard
                  title="CAPEX Pipeline"
                  value={formatCurrency(pipelineData.pipeline.total_capex)}
                  icon={TrendingUp}
                  color="blue"
                />
                <KPICard
                  title="Taux Global"
                  value={`${pipelineData.funnel.go > 0 ? ((pipelineData.funnel.go / pipelineData.funnel.created) * 100).toFixed(0) : 0}%`}
                  subtitle="GO / Créés"
                  icon={Target}
                  color="purple"
                />
              </div>

              {/* Funnel */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Target className="w-5 h-5 text-blue-500" />
                    Entonnoir de Conversion
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {[
                      { label: 'Créés', value: pipelineData.funnel.created, color: 'bg-slate-200', width: '100%' },
                      { label: 'Validés', value: pipelineData.funnel.validated, color: 'bg-blue-400', width: `${(pipelineData.funnel.validated / pipelineData.funnel.created) * 100}%` },
                      { label: 'Décidés', value: pipelineData.funnel.decided, color: 'bg-purple-400', width: `${(pipelineData.funnel.decided / pipelineData.funnel.created) * 100}%` },
                      { label: 'GO', value: pipelineData.funnel.go, color: 'bg-emerald-500', width: `${(pipelineData.funnel.go / pipelineData.funnel.created) * 100}%` }
                    ].map((step, idx) => (
                      <div key={step.label} className="flex items-center gap-4">
                        <div className="w-20 text-sm font-medium text-slate-600">{step.label}</div>
                        <div className="flex-1 bg-slate-100 dark:bg-slate-800 rounded-full h-8 overflow-hidden">
                          <div 
                            className={`h-full ${step.color} transition-all duration-500 flex items-center justify-end pr-3`}
                            style={{ width: step.width }}
                          >
                            <span className="text-white font-bold text-sm">{step.value}</span>
                          </div>
                        </div>
                        <div className="w-16 text-right text-sm text-slate-500">
                          {pipelineData.funnel.created > 0 ? ((step.value / pipelineData.funnel.created) * 100).toFixed(0) : 0}%
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Alerts */}
              {pipelineData.alerts.length > 0 && (
                <Card className="border-amber-200 bg-amber-50/50">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2 text-amber-700">
                      <AlertTriangle className="w-5 h-5" />
                      Dossiers en attente depuis plus de 7 jours
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {pipelineData.alerts.map(alert => (
                        <div 
                          key={alert.id}
                          className="flex items-center justify-between p-3 bg-white dark:bg-slate-800 rounded-lg border border-amber-200 cursor-pointer hover:bg-amber-50 transition-colors"
                          onClick={() => navigate(`/simulation/${alert.id}`)}
                        >
                          <div>
                            <p className="font-medium text-sm">{alert.project_name}</p>
                            <p className="text-xs text-slate-500">{alert.retailer} {alert.city}</p>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <p className="text-sm font-semibold text-amber-600">{alert.days_pending} jours</p>
                              <p className="text-xs text-slate-400">{formatCurrency(alert.ca)}</p>
                            </div>
                            <ChevronRight className="w-4 h-4 text-slate-400" />
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </TabsContent>

        {/* Tab 4: Operational */}
        <TabsContent value="operational" className="space-y-6">
          {operationalData && (
            <>
              {/* Processing Times */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <KPICard
                  title="Création → Validation"
                  value={`${operationalData.processing_times.avg_creation_to_validation_days} jours`}
                  icon={Clock}
                  color="blue"
                />
                <KPICard
                  title="Validation → Décision"
                  value={`${operationalData.processing_times.avg_validation_to_decision_days} jours`}
                  icon={Clock}
                  color="purple"
                />
                <KPICard
                  title="Délai Total Moyen"
                  value={`${operationalData.processing_times.total_avg_days} jours`}
                  icon={Clock}
                  color="amber"
                />
              </div>

              <div className="grid lg:grid-cols-2 gap-6">
                {/* Recent Activity */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Activity className="w-5 h-5 text-blue-500" />
                      Activité Récente
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3 max-h-[400px] overflow-y-auto">
                      {operationalData.recent_activity.map((activity, idx) => (
                        <div key={idx} className="flex items-start gap-3 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                          <div className={`w-2 h-2 rounded-full mt-2 ${
                            activity.action === 'validated' ? 'bg-blue-500' :
                            activity.action === 'approved' ? 'bg-emerald-500' :
                            activity.action === 'rejected' ? 'bg-red-500' :
                            activity.action === 'created' ? 'bg-purple-500' :
                            'bg-slate-400'
                          }`} />
                          <div className="flex-1">
                            <p className="text-sm font-medium">{activity.project_name}</p>
                            <p className="text-xs text-slate-500">
                              {activity.action} par {activity.user}
                            </p>
                            <p className="text-xs text-slate-400 mt-1">
                              {new Date(activity.timestamp).toLocaleString('fr-FR')}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Email History */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Mail className="w-5 h-5 text-emerald-500" />
                      Emails Envoyés
                    </CardTitle>
                    {/* Filter buttons */}
                    <div className="flex flex-wrap gap-2 mt-2">
                      <Button 
                        size="sm" 
                        variant={emailFilter === 'all' ? 'default' : 'outline'}
                        onClick={() => setEmailFilter('all')}
                        className="text-xs h-7"
                      >
                        Tous
                      </Button>
                      <Button 
                        size="sm" 
                        variant={emailFilter === 'validation' ? 'default' : 'outline'}
                        onClick={() => setEmailFilter('validation')}
                        className="text-xs h-7 bg-blue-500 hover:bg-blue-600"
                      >
                        📧 Demandes CAPEX
                      </Button>
                      <Button 
                        size="sm" 
                        variant={emailFilter === 'admin_decision' ? 'default' : 'outline'}
                        onClick={() => setEmailFilter('admin_decision')}
                        className="text-xs h-7 bg-emerald-500 hover:bg-emerald-600"
                      >
                        ✅ Décisions GO/NO GO
                      </Button>
                      <Button 
                        size="sm" 
                        variant={emailFilter === 'counter_proposal' ? 'default' : 'outline'}
                        onClick={() => setEmailFilter('counter_proposal')}
                        className="text-xs h-7 bg-orange-500 hover:bg-orange-600"
                      >
                        🔄 Contre-propositions
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3 max-h-[400px] overflow-y-auto">
                      {operationalData.email_history
                        .filter(email => emailFilter === 'all' || email.type === emailFilter)
                        .map((email, idx) => (
                        <div key={idx} className="flex items-start gap-3 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                          <div className={`p-1.5 rounded-full ${
                            email.status === 'sent' ? 'bg-emerald-100 text-emerald-600' : 'bg-red-100 text-red-600'
                          }`}>
                            {email.status === 'sent' ? <CheckCircle className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-medium truncate">{email.subject}</p>
                            <p className="text-xs text-slate-500">{email.recipient}</p>
                            <p className="text-xs text-slate-400 mt-1">
                              {new Date(email.sent_at).toLocaleString('fr-FR')}
                            </p>
                          </div>
                          <Badge variant="outline" className={`text-[10px] ${
                            email.type === 'validation' ? 'bg-blue-100 text-blue-700 border-blue-300' :
                            email.type === 'admin_decision' ? 'bg-emerald-100 text-emerald-700 border-emerald-300' :
                            email.type === 'counter_proposal' ? 'bg-orange-100 text-orange-700 border-orange-300' :
                            ''
                          }`}>
                            {email.type === 'validation' ? 'Demande CAPEX' :
                             email.type === 'admin_decision' ? 'Décision' :
                             email.type === 'counter_proposal' ? 'Contre-prop.' :
                             email.type}
                          </Badge>
                        </div>
                      ))}
                      {operationalData.email_history.filter(email => emailFilter === 'all' || email.type === emailFilter).length === 0 && (
                        <p className="text-center text-slate-400 py-4">Aucun email trouvé pour ce filtre</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </>
          )}
        </TabsContent>

        {/* Tab 5: Carte */}
        <TabsContent value="carte" className="space-y-6">
          {mapData.length === 0 && !loadingMap && (
            <div className="text-center py-4">
              <Button onClick={fetchMapData} className="gap-2">
                <MapPin className="w-4 h-4" /> Charger la carte des projets
              </Button>
            </div>
          )}
          {loadingMap && (
            <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-blue-500" /></div>
          )}
          {mapData.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-blue-500" />
                  Carte des projets CAPEX ({mapData.length} projets)
                </CardTitle>
                <CardDescription>Vue géographique de tous les projets. Cliquez sur un marqueur pour voir les détails.</CardDescription>
              </CardHeader>
              <CardContent>
                {/* Legend */}
                <div className="flex items-center gap-4 mb-4 flex-wrap">
                  {[
                    { label: 'Signé', color: '#10B981' },
                    { label: 'GO', color: '#3B82F6' },
                    { label: 'En attente', color: '#F59E0B' },
                    { label: 'NO GO', color: '#EF4444' },
                    { label: 'Perdu', color: '#64748B' }
                  ].map(s => (
                    <div key={s.label} className="flex items-center gap-1.5">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: s.color }} />
                      <span className="text-xs text-slate-600">{s.label}</span>
                    </div>
                  ))}
                </div>
                
                {/* Map container with iframe to Google Maps embed */}
                <div className="relative w-full h-[500px] rounded-lg overflow-hidden border bg-slate-100">
                  {/* Fallback: Table view with location data */}
                  <div className="h-full overflow-auto">
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 bg-white z-10">
                        <tr className="border-b">
                          <th className="text-left p-3">Statut</th>
                          <th className="text-left p-3">Projet</th>
                          <th className="text-left p-3">Enseigne</th>
                          <th className="text-left p-3">Ville</th>
                          <th className="text-left p-3">Adresse</th>
                          <th className="text-right p-3">CAPEX</th>
                          <th className="text-right p-3">CA Prévi.</th>
                        </tr>
                      </thead>
                      <tbody>
                        {mapData
                          .sort((a, b) => {
                            const order = { signed: 0, go: 1, pending: 2, nogo: 3, lost: 4 };
                            return (order[a.status] || 5) - (order[b.status] || 5);
                          })
                          .map(p => {
                            const statusColors = { signed: '#10B981', go: '#3B82F6', pending: '#F59E0B', nogo: '#EF4444', lost: '#64748B' };
                            const statusLabels = { signed: 'Signé', go: 'GO', pending: 'En attente', nogo: 'NO GO', lost: 'Perdu' };
                            return (
                              <tr key={p.id} className="border-b hover:bg-slate-50 cursor-pointer" onClick={() => navigate(`/simulation/${p.id}`)}>
                                <td className="p-3">
                                  <span className="px-2 py-0.5 rounded-full text-white text-[10px] font-bold" style={{ backgroundColor: statusColors[p.status] || '#64748B' }}>
                                    {statusLabels[p.status] || p.status}
                                  </span>
                                </td>
                                <td className="p-3 font-medium">{p.project_name}</td>
                                <td className="p-3">{p.retailer}</td>
                                <td className="p-3">{p.city}</td>
                                <td className="p-3 text-xs text-slate-500 max-w-[200px] truncate">{p.address}</td>
                                <td className="p-3 text-right font-medium">{p.total_capex ? `${(p.total_capex/1000).toFixed(0)}k€` : '—'}</td>
                                <td className="p-3 text-right">{p.ca ? `${(p.ca/1000).toFixed(0)}k€` : '—'}</td>
                              </tr>
                            );
                          })}
                      </tbody>
                    </table>
                  </div>
                </div>
                
                {/* Stats by city */}
                <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {(() => {
                    const byCityMap = {};
                    mapData.forEach(p => {
                      const city = p.city || 'Inconnu';
                      if (!byCityMap[city]) byCityMap[city] = { count: 0, signed: 0 };
                      byCityMap[city].count++;
                      if (p.status === 'signed') byCityMap[city].signed++;
                    });
                    return Object.entries(byCityMap)
                      .sort((a, b) => b[1].count - a[1].count)
                      .slice(0, 8)
                      .map(([city, data]) => (
                        <div key={city} className="p-3 bg-slate-50 rounded-lg text-center">
                          <p className="font-bold text-lg text-slate-800">{data.count}</p>
                          <p className="text-xs text-slate-500">{city}</p>
                          {data.signed > 0 && <Badge className="mt-1 bg-emerald-100 text-emerald-700 text-[10px]">{data.signed} signé{data.signed > 1 ? 's' : ''}</Badge>}
                        </div>
                      ));
                  })()}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
